import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, authApi, getToken, setToken } from '../../lib/api';
import { verifyOtp as checkOtp } from '../../lib/auth';
import type { LoginResponse, User } from '../../lib/types';

/**
 * Who is signed in. Holds the session (the token in localStorage, the user
 * from /auth/me) and the sign-in steps. What that person may open is not
 * decided here: see AdminContext, which builds on this.
 */
interface AuthValue {
  user: User | null;
  loading: boolean;
  /**
   * Step one: checks the user name and password. Nobody is signed in yet —
   * the session is held back until verifyOtp accepts a code.
   */
  login: (email: string, password: string) => Promise<void>;
  /** True between a correct password and a correct code. */
  otpPending: boolean;
  /** Step two: rejects on a wrong code, signs the person in on a right one. */
  verifyOtp: (code: string) => Promise<void>;
  /** Backs out of step two and forgets the half-finished sign-in. */
  cancelOtp: () => void;
  logout: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => Boolean(getToken()));
  const [otpPending, setOtpPending] = useState(false);

  // The answer to a correct password, kept in memory only until the code is
  // accepted. Storing the token any earlier would let a page reload skip the
  // code: the session restore below would find it and sign the person in.
  const pending = useRef<LoginResponse | null>(null);

  // Restore the session on first paint when a token is already stored.
  useEffect(() => {
    if (!getToken()) return;

    let cancelled = false;

    authApi
      .me()
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        setToken(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    pending.current = await authApi.login(email, password);
    setOtpPending(true);
  }, []);

  const verifyOtp = useCallback(async (code: string) => {
    const res = pending.current;
    if (!res) throw new ApiError('Your sign-in expired. Please sign in again.', 401);

    await checkOtp(code);

    pending.current = null;
    setOtpPending(false);
    setToken(res.token);
    setUser(res.user);
  }, []);

  const cancelOtp = useCallback(() => {
    pending.current = null;
    setOtpPending(false);
  }, []);

  const logout = useCallback(() => {
    // Tell the server first so the session is really gone, but never block the
    // sign-out on it: the client forgets the token either way.
    authApi.logout().catch(() => undefined);

    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, otpPending, verifyOtp, cancelOtp, logout }),
    [user, loading, login, otpPending, verifyOtp, cancelOtp, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
